import React, {
  ReactNode,
  RefObject,
} from 'react';
import {
  FormProvider,
} from 'react-hook-form';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import Controller from 'core/components/FormController';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  PrimaryButton,
} from 'views/EMR/components/PCC/Walkthrough/components/ContentPrimitives';
import ConfigureFeeSchedule from 'views/EMR/components/PCC/Walkthrough/components/ConfigureFeeSchedule';
import PageIntroduction from 'views/EMR/components/PCC/Walkthrough/components/PageIntroduction';
import ProviderLookup from 'views/EMR/components/ProviderLookup';
import type {
  ConfigurePageDefinition,
} from 'views/EMR/components/PCC/Walkthrough/types';
import type {
  UsePCCSettingsFormResult,
} from 'views/EMR/Settings/PCC/usePCCSettingsForm';

type Props = {
  page: ConfigurePageDefinition;
  headingRef: RefObject<HTMLHeadingElement | null>;
  form: UsePCCSettingsFormResult;
};

type FieldLabelProps = {
  htmlFor: string;
  label: string;
};

function FieldLabel({
  htmlFor,
  label,
}: FieldLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-[15px] font-bold text-[#0F1B2D]"
    >
      {label}
      {' '}
      <span className="text-red-400" aria-hidden="true">*</span>
    </label>
  );
}

type ProgramCardProps = {
  title: string;
  titleId: string;
  children: ReactNode;
  className?: string;
};

function ProgramCard({
  title,
  titleId,
  children,
  className,
}: ProgramCardProps) {
  return (
    <section
      aria-labelledby={titleId}
      className={className ?? 'flex flex-col gap-3.5 rounded-2xl border border-[#E2E8F0] bg-white px-6 py-[22px]'}
    >
      <h2
        id={titleId}
        className="m-0 text-[17px] font-bold text-[#0F1B2D]"
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function ConfigurePage({
  page,
  headingRef,
  form,
}: Props) {
  const {
    formProps,
    control,
    handleSubmit,
    isValid,
    isLoadingDefaults,
    isSaving,
    isFormDisabled,
    showCcmFeeSchedule,
    isValidFeeSchedule,
    onSubmit,
  } = form;

  return (
    <FormProvider {...formProps}>
      <form
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-12 lg:py-7">
          <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-[22px]">
            <PageIntroduction
              eyebrow={page.introduction.eyebrow}
              heading={page.introduction.heading}
              description={page.introduction.description}
              headingRef={headingRef}
            />

            {isLoadingDefaults ? (
              <div className="flex justify-center py-16">
                <Spinner size={24} />
              </div>
            ) : (
              <div className="flex flex-col gap-7">
                <div className="grid grid-cols-1 items-start gap-7 lg:grid-cols-2">
                  <ProgramCard
                    title="TEMPO"
                    titleId="pcc-configure-tempo-title"
                  >
                    <div className="flex flex-col gap-2">
                      <FieldLabel
                        htmlFor="tempo-provider"
                        label="Provider"
                      />
                      <Controller
                        name="tempoProvider"
                        control={control}
                        rules={{ required: 'Select TEMPO provider' }}
                        render={({ field, fieldState: { invalid } }) => (
                          <Wrapper
                            id="tempo-provider"
                            error={invalid}
                            showErrorText={false}
                          >
                            <ProviderLookup
                              {...field}
                              id="tempo-provider"
                              disabled={isFormDisabled}
                              placeholder="Select doctor"
                              className="!h-11"
                            />
                          </Wrapper>
                        )}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <FieldLabel
                        htmlFor="tempo-suicide-alert-assignee"
                        label="Suicide Alert Assignee"
                      />
                      <Controller
                        name="suicideAlertAssignee"
                        control={control}
                        rules={{ required: 'Select suicide alert assignee' }}
                        render={({ field, fieldState: { invalid } }) => (
                          <Wrapper
                            id="tempo-suicide-alert-assignee"
                            error={invalid}
                            showErrorText={false}
                          >
                            <ProviderLookup
                              {...field}
                              id="tempo-suicide-alert-assignee"
                              disabled={isFormDisabled}
                              placeholder="Select assignee"
                              onlyDoctors={false}
                              className="!h-11"
                            />
                          </Wrapper>
                        )}
                      />
                    </div>
                    <h3 className="m-0 text-[15px] font-bold text-[#0F1B2D]">
                      Fee schedule
                    </h3>
                    <Controller
                      name="tempoFeeSchedule"
                      control={control}
                      rules={{
                        validate: (feeSchedule) => (
                          isValidFeeSchedule(feeSchedule)
                          || 'Provide a valid fee schedule for TEMPO'
                        ),
                      }}
                      render={({ field, fieldState: { invalid } }) => (
                        <ConfigureFeeSchedule
                          value={field.value}
                          onChange={field.onChange}
                          disabled={isFormDisabled}
                          error={invalid}
                          ariaLabel="TEMPO fee schedule"
                        />
                      )}
                    />
                  </ProgramCard>

                  <ProgramCard
                    title="RTM"
                    titleId="pcc-configure-rtm-title"
                  >
                    <h3 className="m-0 text-[15px] font-bold text-[#0F1B2D]">
                      Fee schedule
                    </h3>
                    <Controller
                      name="rtmFeeSchedule"
                      control={control}
                      rules={{
                        validate: (feeSchedule) => (
                          isValidFeeSchedule(feeSchedule)
                          || 'Provide a valid fee schedule for RTM'
                        ),
                      }}
                      render={({ field, fieldState: { invalid } }) => (
                        <ConfigureFeeSchedule
                          value={field.value}
                          onChange={field.onChange}
                          disabled={isFormDisabled}
                          error={invalid}
                          ariaLabel="RTM fee schedule"
                        />
                      )}
                    />
                  </ProgramCard>
                </div>

                {showCcmFeeSchedule ? (
                  <ProgramCard
                    title="CCM"
                    titleId="pcc-configure-ccm-title"
                  >
                    <h3 className="m-0 text-[15px] font-bold text-[#0F1B2D]">
                      Fee schedule
                    </h3>
                    <Controller
                      name="ccmFeeSchedule"
                      control={control}
                      rules={{
                        validate: (feeSchedule) => (
                          isValidFeeSchedule(feeSchedule)
                          || 'Provide a valid fee schedule for CCM'
                        ),
                      }}
                      render={({ field, fieldState: { invalid } }) => (
                        <ConfigureFeeSchedule
                          value={field.value}
                          onChange={field.onChange}
                          disabled={isFormDisabled}
                          error={invalid}
                          ariaLabel="CCM fee schedule"
                        />
                      )}
                    />
                  </ProgramCard>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[#E2E8F0] bg-white px-4 py-3 sm:min-h-20 sm:px-8 sm:py-0">
          <span className="text-sm text-[#475569]">
            {page.footerHelper}
          </span>
          <DisabledFormTooltip>
            <PrimaryButton
              disabled={!isValid || isFormDisabled}
              className="!h-[52px] !px-10 !text-[17px]"
              onClick={handleSubmit(onSubmit)}
            >
              {isSaving ? page.pendingLabel : page.saveLabel}
            </PrimaryButton>
          </DisabledFormTooltip>
        </div>
      </form>
    </FormProvider>
  );
}
